import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import axios from 'axios';
import CONFIG from '../../config';
import './AdminDashboard.css';

const HardwareGrid = () => {
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchDevices();
    }, []);

    const fetchDevices = async () => {
        try {
            const resp = await axios.get(`${CONFIG.API_URL}/api/devices`);
            setDevices(resp.data);
            setLoading(false);
        } catch (err) {
            console.error('Failed to fetch devices:', err);
            setLoading(false);
        }
    };

    if (loading) return <div className="main-view">Loading hardware assets...</div>;

    return (
        <div className="main-view">
            <div style={{ marginBottom: '32px' }}>
                <h2 style={{ fontSize: '28px', fontWeight: 800 }}>Hardware Infrastructure</h2>
                <p style={{ color: 'var(--text-muted)' }}>Monitor and manage biometric, RFID, and vision-based hardware assets.</p>
            </div>

            {devices.length === 0 ? (
                <div className="glass-card" style={{ 
                    textAlign: 'center', 
                    padding: '80px 40px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '20px',
                    background: 'rgba(165, 42, 42, 0.02)'
                }}>
                    <div style={{ 
                        width: '80px', 
                        height: '80px', 
                        background: 'rgba(128, 0, 0, 0.05)', 
                        borderRadius: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--primary)'
                    }}>
                        <Icon icon="ion:hardware-chip-outline" width="40" />
                    </div>
                    <div>
                        <h3 style={{ fontSize: '20px', fontWeight: 800, marginBottom: '8px' }}>No Hardware Detected</h3>
                        <p style={{ color: 'var(--text-muted)', maxWidth: '400px' }}>
                            We couldn't find any connected hardware devices. Please ensure your local terminals are powered on and connected to the gateway.
                        </p>
                    </div>
                    <button className="btn-primary" onClick={fetchDevices} style={{ width: 'auto', padding: '12px 32px' }}>
                        Refresh Connection
                    </button>
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '24px' }}>
                    {devices.map(device => (
                        <DeviceCard key={device.device_id} device={device} />
                    ))}
                    
                    {/* Add New Device Placeholder */}
                    <div className="glass-card" style={{ 
                        border: '2px dashed var(--border-light)', 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        padding: '40px',
                        cursor: 'pointer',
                        background: 'transparent',
                        boxShadow: 'none'
                    }}>
                        <Icon icon="ion:add-circle-outline" width="48" color="var(--border-light)" />
                        <span style={{ marginTop: '12px', fontWeight: 700, color: 'var(--text-muted)' }}>Provision New Device</span>
                    </div>
                </div>
            )}
        </div>
    );
};

const DeviceCard = ({ device }) => {
    const isOnline = device.status === 'online';

    return (
        <div className="glass-card" style={{ padding: '0', overflow: 'hidden' }}>
            <div style={{ 
                padding: '24px', 
                background: isOnline ? 'rgba(16, 185, 129, 0.05)' : 'rgba(239, 68, 68, 0.05)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start'
            }}>
                <div style={{ 
                    width: '48px', 
                    height: '48px', 
                    background: 'white', 
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)'
                }}>
                    <Icon 
                        icon={device.type === 'VISION' ? 'ion:videocam-outline' : device.type === 'FINGERPRINT' ? 'ion:finger-print-outline' : 'ion:card-outline'} 
                        width="24" 
                        color="var(--primary)" 
                    />
                </div>
                <div style={{ 
                    padding: '4px 12px', 
                    borderRadius: '100px', 
                    fontSize: '11px', 
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    background: isOnline ? '#dcfce7' : '#fee2e2',
                    color: isOnline ? '#166534' : '#991b1b',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                }}>
                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: isOnline ? '#10b981' : '#ef4444' }}></div>
                    {device.status}
                </div>
            </div>
            
            <div style={{ padding: '24px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: 800, marginBottom: '4px' }}>{device.name}</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--text-muted)', marginBottom: '20px' }}>
                    <Icon icon="ion:barcode-outline" /> {device.device_id}
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', paddingTop: '20px', borderTop: '1px solid var(--border-light)' }}>
                    <div>
                        <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>TYPE</div>
                        <div style={{ fontSize: '13px', fontWeight: 600 }}>{device.type}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>LAST SYNC</div>
                        <div style={{ fontSize: '13px', fontWeight: 600 }}>{new Date(device.last_sync).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HardwareGrid;
